import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useDashboard } from './DashboardProvider';
import { usePagination } from '../hooks/usePagination';
import { useUserOperations } from '../hooks/useUserOperations';
import { TCreateUserProp } from '../services/userService';

type TProps = {
  children: ReactNode;
};
export type TRole = {
  id: number;
  label: string;
  slug: string
}
type TContext = {
  selectedProfile: TUser;
  usersList: TUsersList,
  limit: number;
  offset: number;
  roles: TRole[];
  alertMsgs: Partial<Record<string, string[]>>;
  usersLoading: boolean;
  setUsersList: Dispatch<SetStateAction<{ count: number; results: TUser[] }>>;
  setSelectedProfile: Dispatch<SetStateAction<TUser>>;
  getRoles: () => void;
  setOffset: Dispatch<SetStateAction<number>>;
  setLimit: Dispatch<SetStateAction<number>>;
  CreateUser: (params: TCreateUserProp) => void;
  updateUserFullName: ({ id, firstName, lastName }: { id: number, firstName: string, lastName: string }) => void;
  updateUserPhoneNumber: ({ id, phone }: { id: number, phone: string }) => void;
  updateUserStatus: ({ id, status }: { id: number, status: boolean }) => void;
  updateUserRole: ({ id, role }: { id: number, role: string | number }) => void;
};
export type TUser = {
  profile_photo_thumbnail?: string;
  id: number;
  email: string;
  is_active: boolean;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  clinic_role: number | string;
  two_factor_enabled: boolean;
  language: string
};
export type TUsersList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TUser[];
};

const UsersContext = createContext<TContext>({} as TContext);


export const UsersProvider = ({ children }: TProps) => {
  const [alertMsgs, setAlertMessages] = useState({});
  const [selectedProfile, setSelectedProfile] = useState<TUser>({} as TUser);
  const { setCloseUpdateModal } = useDashboard();
  const [usersLoading, setUsersLoading] = useState(false)

  const { limit, offset, setLimit, setOffset } = usePagination(10);
  const {
      usersList,
      roles,
      setUsersList,
      createUser: createUserOperation,
      updateUserFullName: updateUserNameOperation,
      updateUserPhoneNumber: updateUserPhoneOperation,
      updateUserStatus: updateUserStatusOperation,
      updateUserRole: updateUserRoleOperation,
      getRoles: fetchRoles,
  } = useUserOperations({
      limit,
      offset,
      setSelectedProfile,
      setUsersLoading,
      setCloseUpdateModal,
      setAlertMessages,
  });

  const CreateUser = async (data: TCreateUserProp) => {
      await createUserOperation(data);
  };

  const updateUserFullName = async ({ id, firstName, lastName }: { id: number; firstName: string; lastName: string }) => {
      await updateUserNameOperation(id, firstName, lastName);
  };

  const updateUserPhoneNumber = async ({ id, phone }: { id: number; phone: string }) => {
      await updateUserPhoneOperation(id, phone);
  };

  const updateUserStatus = async ({ id }: { id: number }) => {
      await updateUserStatusOperation(id);
  };

  const updateUserRole = async ({ id, role }: { id: number; role: string | number }) => {
      await updateUserRoleOperation(id, role);
  };

  const getRoles = async () => {
      await fetchRoles(setAlertMessages);
  };

  const contextValue = useMemo(() => ({
      usersList,
      limit,
      roles,
      offset,
      selectedProfile,
      alertMsgs,
      usersLoading,
      setOffset,
      setUsersList,
      setSelectedProfile,
      getRoles,
      updateUserRole,
      updateUserFullName,
      updateUserStatus,
      updateUserPhoneNumber,
      setLimit,
      CreateUser
  }), [usersList, roles, selectedProfile, limit, usersLoading, alertMsgs, offset,]);

  return (
      <UsersContext.Provider value={contextValue}>
          {children}
      </UsersContext.Provider>
  );
};


export const useUsers = () => {
  return useContext(UsersContext);
};
